import { useEffect, useState, useCallback } from "react";

const RECAPTCHA_SITE_KEY = "6LdXqRosAAAAAHMMo995CdMlwkpP-3CqwJi9Np7u";
const SCRIPT_ID = "recaptcha-enterprise-script";

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

export function useRecaptchaEnterprise() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if script already exists
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      setIsLoaded(true);
      return;
    }

    // Load the reCAPTCHA Enterprise script
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(() => {
          setIsLoaded(true);
        });
      } else {
        setError("reCAPTCHA Enterprise failed to load");
      }
    };

    script.onerror = () => {
      setError("Failed to load reCAPTCHA Enterprise script");
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const scriptElement = document.getElementById(SCRIPT_ID);
      if (scriptElement) {
        scriptElement.remove();
      }
    };
  }, []);

  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | null> => {
      if (!isLoaded || !window.grecaptcha?.enterprise) {
        setError("reCAPTCHA Enterprise not loaded");
        return null;
      }

      try {
        const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
          action,
        });
        return token;
      } catch (err) {
        console.error("reCAPTCHA execution error:", err);
        setError("Failed to execute reCAPTCHA");
        return null;
      }
    },
    [isLoaded]
  );

  return {
    executeRecaptcha: isLoaded ? executeRecaptcha : null,
    isLoaded,
    error,
  };
}
